import { useEffect, useRef } from 'react'

import { Segment, User } from '../types'
import { setCaretPosition } from '../utils/domUtils'
import { normalizeSegments } from '../utils/segmentUtils'

import { useEditorDOM } from './useEditorDOM'
import { useEditorHandlers } from './useEditorHandlers'
import { useEditorState } from './useEditorState'
import { useMentionLogic } from './useMentionLogic'

export const useMentionEditor = ({ users, initialValue, isAnnotating, onFocusChange }: { users: User[], initialValue?: string, isAnnotating?: boolean, onFocusChange?: (isFocused: boolean) => void; }) => {
  // 1. All state is managed by this hook
  const state = useEditorState({ users, initialValue })
  const popupRef = useRef<HTMLDivElement>(null)

  const focusSegment = (index: number, position: 'start' | 'end' = 'end') => {
    if (index < 0 || index >= state.segments.length) {
      return
    }

    const segment = state.segments[index]
    if (!segment) {
      return
    }

    state.setActiveIndex(index)
    state.setIsNavigating(false)

    requestAnimationFrame(() => {
      const element = state.elRefs.current.get(segment.id)
      if (element) {
        element.focus()
        const offset = position === 'end' ? element.textContent?.length || 0 : 0
        setCaretPosition(element, offset)
      }
    })
  }

  const showPopupForSegment = (segment: Segment) => {
    if (segment.type === 'mention' || segment.type === 'mention_in_progress') {
      if (
        state.isMentionPopupOpen &&
        state.mentionQuery === segment.content.substring(1)
      ) {
        return // Already open for this mention, do nothing.
      }
      state.setMentionQuery(segment.content.substring(1))
      state.setIsMentionPopupOpen(true)
    } else {
      state.setIsMentionPopupOpen(false)
      state.setMentionQuery('')
    }
  }

  // 2. A function that needs access to the state's elRefs
  const selectAllContent = () => {
    state.setIsNavigating(true)
    state.selectionRequestRef.current = { type: 'all' }
  }

  // 4. Mention logic hook is created ONCE
  const mentionLogic = useMentionLogic({
    ...state,
    editorRef: state.editorRef,
  })

  // NEW: Centralized function to commit a mention
  const commitMention = (user: User) => {
    const {
      segments,
      activeIndex,
      setSegments,
      setActiveIndex,
      setIsMentionPopupOpen,
      setMentionQuery,
      nextId,
      elRefs,
    } = state

    const mentionSegmentInProgress = segments[activeIndex]

    const finalizedMentionSegment: Segment = {
      ...mentionSegmentInProgress,
      type: 'mention',
      content: `@${user.name}`,
      isModified: false,
    }

    const spaceSegment: Segment = {
      id: `seg-${nextId.current++}`,
      type: 'text',
      content: ' ',
    }

    const newSegments = [...segments]
    newSegments[activeIndex] = finalizedMentionSegment
    newSegments.splice(activeIndex + 1, 0, spaceSegment)

    const normalized = normalizeSegments(newSegments)
    const finalNewIndex = normalized.findIndex(s => s.id === spaceSegment.id)

    // Update state to trigger re-render
    setSegments(normalized)
    setActiveIndex(finalNewIndex !== -1 ? finalNewIndex : normalized.length - 1)
    setIsMentionPopupOpen(false)
    setMentionQuery('')
    state.setFilteredUsers(users)

    // Use requestAnimationFrame to focus and set cursor AFTER the DOM has updated
    requestAnimationFrame(() => {
      const element = elRefs.current.get(spaceSegment.id)
      if (element) {
        element.focus()
        setCaretPosition(element, 1) // Place cursor after the space
      }
    })
  }

  // 3. All event handlers are managed by this hook, receiving mentionLogic
  const handlers = useEditorHandlers({
    ...state,
    editorRef: state.editorRef,
    popupRef,
    mentionLogic,
    commitMention,
    selectAllContent,
    showPopupForSegment,
    isAnnotating,
    onFocusChange,
  })

  // 5. All DOM side-effects are managed by this hook
  useEditorDOM({
    state: {
      segments: state.segments,
      activeIndex: state.activeIndex,
      isNavigating: state.isNavigating,
      focusRequestCounter: state.focusRequestCounter,
      globalCursorPos: state.globalCursorPos,
    },
    refs: {
      editorRef: state.editorRef,
      elRefs: state.elRefs,
      nextCursorPos: state.nextCursorPos,
      isSelectionProgrammatic: state.isSelectionProgrammatic,
      selectionRequestRef: state.selectionRequestRef,
    },
  })

  // 5. NEW: Effect to handle exiting navigation mode on click
  useEffect(() => {
    const handleSelectionChange = () => {
      if (state.isSelectionProgrammatic.current) {
        state.isSelectionProgrammatic.current = false // Reset the flag and exit.
        return
      }

      if (state.isNavigating) {
        return
      }

      const selection = window.getSelection()
      if (
        !selection ||
        !state.editorRef.current?.contains(selection.anchorNode)
      ) {
        return
      }

      // If selection is not collapsed, we are selecting text, hide popup.
      if (!selection.isCollapsed) {
        state.setIsMentionPopupOpen(false)
        return
      }

      const range = selection.getRangeAt(0)
      let node = range.startContainer
      while (node && node !== state.editorRef.current) {
        if (
          node.nodeType === Node.ELEMENT_NODE &&
          (node as HTMLElement).dataset.segmentId
        ) {
          const segmentId = (node as HTMLElement).dataset.segmentId
          const segment = state.segments.find(s => s.id === segmentId)

          if (segment) {
            showPopupForSegment(segment)
          }

          // It's a different type of segment, exit navigation mode
          const targetIndex = state.segments.findIndex(
            (s: Segment) => s.id === segmentId,
          )
          if (targetIndex !== -1) {
            state.setActiveIndex(targetIndex)
            state.setIsNavigating(false)
            state.nextCursorPos.current = range.startOffset
          }
          return
        }
        node = node.parentNode
      }
    }

    document.addEventListener('selectionchange', handleSelectionChange)
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange)
    }
  }, [state]) // Depend on the entire state object for simplicity

  // 6. Logic for the user info popup on hover (could be another hook later)
  useEffect(() => {
    const {
      // ... existing code ...
    } = state
  }, [state])

  // 7. Return everything the UI component needs
  return {
    ...state,
    ...handlers,
    ...mentionLogic,
    commitMention,
    updateSegmentStyle: state.updateSegmentStyle,
    focusSegment,
    selectAllContent,
  }
} 